import { Card } from "@/components/ui/card";
import { ACCOUNT_TIER_CONFIG } from "./StatusBadge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, AlertTriangle, Sparkles, GripVertical } from "lucide-react";
import { useState, useCallback } from "react";
import {
  DndContext,
  DragOverlay,
  useDroppable,
  useDraggable,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import { DraftOutreachLauncher } from "@/components/campaigns/DraftOutreachLauncher";
import { DncBadge } from "@/components/dnc/DncBadge";
import { useUpdateAccount } from "@/hooks/useTargetUniverse";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import type { TargetUniverseAccount, TargetUniverseStats, KanbanData } from "@/hooks/useTargetUniverse";

const TIER_ORDER = ["new", "cold_target", "warm_target", "active_prospect", "customer", "disqualified"];

const TIER_BORDER_COLORS: Record<string, string> = {
  new: "border-l-gray-300",
  cold_target: "border-l-blue-400",
  warm_target: "border-l-amber-400",
  active_prospect: "border-l-green-400",
  customer: "border-l-emerald-400",
  disqualified: "border-l-red-400",
};

// ── Droppable Column ──────────────────────────────────────────────

function DroppableColumn({
  tier,
  isOver,
  children,
}: {
  tier: string;
  isOver: boolean;
  children: React.ReactNode;
}) {
  const { setNodeRef } = useDroppable({ id: tier });
  const config = ACCOUNT_TIER_CONFIG[tier] || ACCOUNT_TIER_CONFIG.cold;

  return (
    <div
      ref={setNodeRef}
      className={`w-[240px] flex-shrink-0 flex flex-col rounded-lg border transition-colors ${
        isOver ? "bg-primary/5 border-primary/40 ring-1 ring-primary/20" : "bg-muted/30"
      }`}
    >
      {children}
    </div>
  );
}

// ── Draggable Card ────────────────────────────────────────────────

function DraggableAccountCard({
  acct,
  tier,
  isSelected,
  onToggleSelect,
  onReview,
  onDraft,
}: {
  acct: TargetUniverseAccount;
  tier: string;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  onReview: (acct: TargetUniverseAccount) => void;
  onDraft: (acct: TargetUniverseAccount) => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: acct.id,
    data: { tier, account: acct },
  });

  return (
    <Card
      ref={setNodeRef}
      className={`px-2.5 py-1.5 cursor-pointer hover:shadow-md transition-shadow border-l-4 ${
        TIER_BORDER_COLORS[tier] || "border-l-gray-400"
      } ${isSelected ? "ring-2 ring-primary bg-primary/5" : ""} ${
        isDragging ? "opacity-30" : ""
      }`}
      onClick={() => onReview(acct)}
    >
      <div className="flex items-center justify-between gap-1">
        <div
          {...listeners}
          {...attributes}
          className="shrink-0 cursor-grab active:cursor-grabbing p-0.5 -ml-1 text-muted-foreground/50 hover:text-muted-foreground"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </div>
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelect(acct.id)}
          onClick={(e) => e.stopPropagation()}
          className="h-3.5 w-3.5 shrink-0"
        />
        <span className="font-medium text-sm text-foreground truncate flex-1">{acct.name}</span>
        <DncBadge accountId={acct.id} iconOnly className="shrink-0" />
        {acct.contact_count > 0 && (
          <span className="text-[10px] text-muted-foreground shrink-0">{acct.contact_count}c</span>
        )}
        {acct.contact_count > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDraft(acct);
            }}
            title="Draft outreach to top contact"
            className="shrink-0 p-0.5 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary"
          >
            <Sparkles className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {(acct.territory || acct.sales_rep) && (
        <div className="text-[11px] text-muted-foreground truncate">
          {[acct.territory, acct.sales_rep].filter(Boolean).join(" · ")}
        </div>
      )}
      {(acct.notes_stale || acct.needs_scale_review) && (
        <div className="flex items-center gap-1.5 mt-0.5">
          {acct.notes_stale && (
            <span className="text-[10px] text-amber-600 flex items-center gap-0.5" title="Notes older than 180 days">
              <AlertTriangle className="h-3 w-3" />stale
            </span>
          )}
          {acct.needs_scale_review && (
            <span className="text-[10px] text-orange-600 flex items-center gap-0.5" title={`${acct.employee_count ?? '?'} employees — review scale fit`}>
              <AlertTriangle className="h-3 w-3" />&lt;6 emp
            </span>
          )}
        </div>
      )}
    </Card>
  );
}

// ── Overlay Card (follows cursor during drag) ─────────────────────

function OverlayCard({ account }: { account: TargetUniverseAccount }) {
  return (
    <Card className="w-[220px] px-2.5 py-1.5 shadow-lg border-l-4 border-l-primary opacity-90 rotate-1">
      <span className="font-medium text-sm text-foreground truncate block">{account.name}</span>
      {(account.territory || account.sales_rep) && (
        <div className="text-[11px] text-muted-foreground truncate">
          {[account.territory, account.sales_rep].filter(Boolean).join(" · ")}
        </div>
      )}
    </Card>
  );
}

// ── Main Kanban Component ─────────────────────────────────────────

interface TargetUniverseKanbanProps {
  kanbanData?: KanbanData;
  isLoading: boolean;
  onReview: (account: TargetUniverseAccount) => void;
  stats?: TargetUniverseStats | null;
  onLoadMore: (tier: string) => void;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
}

export function TargetUniverseKanban({
  kanbanData,
  isLoading,
  onReview,
  stats,
  onLoadMore,
  selectedIds,
  onToggleSelect,
}: TargetUniverseKanbanProps) {
  const [draftAccount, setDraftAccount] = useState<TargetUniverseAccount | null>(null);
  const [activeAccount, setActiveAccount] = useState<TargetUniverseAccount | null>(null);
  const [overTier, setOverTier] = useState<string | null>(null);

  const updateAccount = useUpdateAccount();
  const { userProfile } = useAuth();
  const performedBy = {
    id: userProfile?.id ?? null,
    name: userProfile?.sales_rep_name || userProfile?.full_name || "User",
  };

  // Require 8px movement before starting drag to avoid accidental drags on click
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const acct = event.active.data.current?.account as TargetUniverseAccount | undefined;
    setActiveAccount(acct || null);
  }, []);

  const handleDragOver = useCallback((event: any) => {
    setOverTier(event.over?.id as string || null);
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      setActiveAccount(null);
      setOverTier(null);

      const { active, over } = event;
      if (!over) return;

      const sourceTier = active.data.current?.tier as string;
      const targetTier = over.id as string;
      const account = active.data.current?.account as TargetUniverseAccount;

      if (sourceTier === targetTier || !account) return;

      // Guard: disqualified requires a reason — open the review panel instead
      if (targetTier === "disqualified") {
        toast.info("Open the account to add a disqualification reason", {
          description: account.name,
        });
        onReview(account);
        return;
      }

      try {
        await updateAccount.mutateAsync({
          accountId: account.id,
          fields: {
            account_tier: targetTier,
            tier_changed_at: new Date().toISOString(),
            tier_changed_by: performedBy.name,
            last_reviewed_at: new Date().toISOString(),
            last_reviewed_by: performedBy.name,
          },
          activityType: "status_changed",
          activityDescription: `Status changed from ${sourceTier} to ${targetTier} (drag)`,
          activityMetadata: {
            old_status: sourceTier,
            new_status: targetTier,
            method: "kanban_drag",
          },
          performedBy,
        });
        const label = ACCOUNT_TIER_CONFIG[targetTier]?.label || targetTier;
        toast.success(`Moved to ${label}`, { description: account.name });
      } catch (err) {
        toast.error("Failed to update status", {
          description: `Could not move ${account.name}`,
        });
      }
    },
    [updateAccount, performedBy, onReview]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        Loading...
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="flex-1 overflow-x-auto">
          <div className="flex gap-2.5 p-4 min-w-max">
            {TIER_ORDER.map((tier) => {
              const config = ACCOUNT_TIER_CONFIG[tier] || ACCOUNT_TIER_CONFIG.new;
              const tierData = kanbanData?.[tier];
              const items = tierData?.accounts || [];
              const totalCount = tierData?.totalCount ?? 0;
              const hasMore = tierData?.hasMore ?? false;

              return (
                <DroppableColumn key={tier} tier={tier} isOver={overTier === tier}>
                  <div className="flex items-center justify-between px-3 py-2 border-b">
                    <span className="text-sm font-semibold text-foreground">{config.label}</span>
                    <span className="text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5">
                      {totalCount.toLocaleString()}
                    </span>
                  </div>
                  <ScrollArea className="flex-1 max-h-[calc(100vh-320px)]">
                    <div className="p-1.5 space-y-1">
                      {items.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-4">No accounts</p>
                      ) : (
                        items.map((acct) => (
                          <DraggableAccountCard
                            key={acct.id}
                            acct={acct}
                            tier={tier}
                            isSelected={selectedIds.has(acct.id)}
                            onToggleSelect={onToggleSelect}
                            onReview={onReview}
                            onDraft={setDraftAccount}
                          />
                        ))
                      )}
                      {hasMore && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-xs h-7 text-muted-foreground"
                          onClick={() => onLoadMore(tier)}
                        >
                          <ChevronDown className="h-3 w-3 mr-1" />
                          Load more ({totalCount - items.length} remaining)
                        </Button>
                      )}
                    </div>
                  </ScrollArea>
                </DroppableColumn>
              );
            })}
          </div>
        </div>

        <DragOverlay dropAnimation={null}>
          {activeAccount ? <OverlayCard account={activeAccount} /> : null}
        </DragOverlay>
      </DndContext>

      <DraftOutreachLauncher
        accountId={draftAccount?.id ?? null}
        accountName={draftAccount?.name ?? null}
        accountDomain={draftAccount?.domain ?? null}
        open={!!draftAccount}
        onOpenChange={(o) => !o && setDraftAccount(null)}
      />
    </div>
  );
}
